import React, { useEffect, useLayoutEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { HighlightStyle, indentUnit, StreamLanguage, syntaxHighlighting } from "@codemirror/language";
import { EditorView, keymap } from "@codemirror/view";
import { Prec } from "@codemirror/state";
import { tags } from "@lezer/highlight";
import PythonTestCases from "../PythonTestCases";
import { normalizeGraderText, runNotebookCellsOnValidator } from "../../../utils/marimoValidator";
import { validateNotebookCellTests } from "../../../utils/marimoNotebookTests";
import { emptyNotebook, marimoPyFromIpynb, parseNotebook, serializeNotebook } from "../../../utils/marimoNotebook";

const SKIP_NAMES = new Set([
  "import", "from", "def", "return", "as", "if", "else", "elif", "for", "while",
  "in", "and", "or", "not", "True", "False", "None", "class", "with", "try",
  "except", "lambda", "pass", "marimo", "mo", "app",
]);

function definedNames(source) {
  const names = [];
  const pattern = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*=/gm;
  let match = pattern.exec(String(source || ""));
  while (match) {
    if (!SKIP_NAMES.has(match[1])) names.push(match[1]);
    match = pattern.exec(String(source || ""));
  }
  return names;
}

function cellLinks(cells) {
  const owner = {};
  const links = [];
  cells.forEach((cell) => {
    if (cell.type !== "code") return;
    const tokens = String(cell.source || "").match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) || [];
    const seen = new Set();
    tokens.forEach((token) => {
      const fromId = owner[token];
      if (!fromId || fromId === cell.id || seen.has(fromId)) return;
      seen.add(fromId);
      links.push({ from: fromId, to: cell.id, name: token });
    });
    definedNames(cell.source).forEach((name) => {
      owner[name] = cell.id;
    });
  });
  return links;
}

function safeImage(data) {
  return typeof data === "string" && data.length > 0 && data.length <= 1600000 && /^[A-Za-z0-9+/=\s]+$/.test(data);
}

function normalizeOutput(text) {
  return normalizeGraderText(text);
}

function actualText(data) {
  const blocks = Array.isArray(data?.outputs) ? data.outputs : [];
  const text = blocks.filter((block) => block.type === "text").map((block) => block.text || "").join("\n");
  return text || data?.output || "";
}

function scoreTests(tests, data) {
  const actual = data?.ok ? actualText(data) : "";
  const failure = data?.ok ? "" : (data?.error || "Validation failed.");
  return (tests || []).map((test, index) => {
    const expected = String(test.Output ?? test.expected ?? "");
    const values = Array.isArray(test.Value) ? test.Value : [];
    const title = String(test.title || test.name || "").trim();
    const description = String(test.description || "");
    const authoredError = String(test.errorMessage || test.error_message || "").trim();
    const expectedNorm = normalizeOutput(expected);
    const actualNorm = normalizeOutput(actual);
    const passed = !!data?.ok && expectedNorm !== "" && (actualNorm === expectedNorm || actualNorm.includes(expectedNorm));
    let error = "";
    if (!passed) {
      if (failure) error = failure;
      else if (authoredError) error = authoredError;
      else if (!expectedNorm) error = "Expected output is empty.";
      else error = "Actual output does not match the expected output.";
    }
    return {
      id: `case-${index}`,
      name: title || `Test case ${index + 1}`,
      description,
      input: values.filter((value) => String(value).trim()).join("\n"),
      expected,
      actual,
      passed,
      error,
      message: passed ? "Output matches this test case." : "",
    };
  });
}

function ValidationReport({ report, onSelect }) {
  const cases = report?.cases || [];
  const selected = cases.find((item) => item.id === report.selectedId) || cases[0];
  const passedCount = cases.filter((item) => item.passed).length;
  if (!cases.length && !report?.message) return null;
  return (
    <div className="mt-2" style={{ border: "1px solid #d0d7de", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
      <div className="d-flex justify-content-between align-items-center px-2 py-1 border-bottom" style={{ background: "#f6f8fa" }}>
        <strong style={{ fontSize: 12 }}>Validation</strong>
        <span className={`badge ${passedCount === cases.length && cases.length ? "bg-success" : "bg-danger"}`}>
          {cases.length ? `${passedCount}/${cases.length} passed` : "No tests"}
        </span>
      </div>
      {report?.message && !cases.length && (
        <div className="p-2 text-muted" style={{ fontSize: 12 }}>{report.message}</div>
      )}
      {cases.length > 0 && (
        <div className="d-flex" style={{ minHeight: 140 }}>
          <div className="border-end" style={{ width: 180, overflow: "auto", background: "#f8f9fa" }}>
            {cases.map((item) => (
              <button
                key={item.id}
                type="button"
                className="btn btn-sm w-100 text-start border-0 rounded-0"
                style={{ background: selected?.id === item.id ? "#fff" : "transparent" }}
                onClick={() => onSelect(item.id)}
              >
                <span className="d-flex justify-content-between align-items-center gap-2">
                  <span className="text-truncate" style={{ fontSize: 12 }}>{item.name}</span>
                  <span className={`badge ${item.passed ? "bg-success" : "bg-danger"}`}>{item.passed ? "Pass" : "Fail"}</span>
                </span>
              </button>
            ))}
          </div>
          {selected && (
            <div className="p-2" style={{ flex: 1, overflow: "auto", fontSize: 12 }}>
              <div className="mb-2">
                <strong>{selected.name}</strong>
                <span className={`badge ms-2 ${selected.passed ? "bg-success" : "bg-danger"}`}>
                  {selected.passed ? "Passed" : "Failed"}
                </span>
              </div>
              <div className="mb-2">
                <strong>Description: </strong>
                {selected.description || <span className="text-muted">No description</span>}
              </div>
              <div className="mb-2">
                <strong>Values</strong>
                <pre className="mb-0 mt-1 p-2 bg-light rounded" style={{ whiteSpace: "pre-wrap" }}>{selected.input || "No values"}</pre>
              </div>
              <div className="mb-2">
                <strong>Expected output</strong>
                <pre className="mb-0 mt-1 p-2 bg-light rounded" style={{ whiteSpace: "pre-wrap" }}>{selected.expected || "No expected output"}</pre>
              </div>
              <div className="mb-2">
                <strong>Actual output</strong>
                <pre className="mb-0 mt-1 p-2 bg-light rounded" style={{ whiteSpace: "pre-wrap" }}>{selected.actual || "No output"}</pre>
              </div>
              {selected.passed && (
                <div className="alert alert-success py-2 mb-0"><strong>Success: </strong>Output matches this test case.</div>
              )}
              {!selected.passed && (
                <div className="alert alert-danger py-2 mb-0"><strong>Error: </strong>{selected.error}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RunOutput({ note }) {
  if (!note) return null;
  const outputs = Array.isArray(note.outputs) ? note.outputs : [];
  return (
    <div className="mt-2" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {note.pending && (
        <div style={{ fontSize: 12, color: "#57606a" }}>{note.text}</div>
      )}
      {!note.pending && !note.ok && (
        <pre style={{ margin: 0, padding: 10, borderRadius: 8, background: "#fff1f0", color: "#b42318", border: "1px solid #ffccc7", whiteSpace: "pre-wrap", fontSize: 12, fontFamily: "Consolas, monospace" }}>
          {note.text}
        </pre>
      )}
      {!note.pending && note.ok && outputs.length === 0 && (
        <div style={{ fontSize: 12, color: "#57606a" }}>{note.text || "(no output)"}</div>
      )}
      {outputs.map((block, index) => {
        if (block.type === "image" && safeImage(block.data)) {
          return (
            <img
              key={`image-${index}`}
              alt="Cell output"
              src={`data:image/png;base64,${block.data}`}
              style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid #d0d7de", background: "#fff" }}
            />
          );
        }
        if (block.type === "table" && Array.isArray(block.columns)) {
          const rows = Array.isArray(block.rows) ? block.rows : [];
          return (
            <div key={`table-${index}`} style={{ overflow: "auto", border: "1px solid #d0d7de", borderRadius: 8, background: "#fff" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "linear-gradient(90deg, #ddf4ff, #dafbe1)" }}>
                    {block.columns.map((column, columnIndex) => (
                      <th key={`${column}-${columnIndex}`} style={{ padding: "6px 8px", textAlign: "left", color: "#0969da", fontWeight: 650 }}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ background: rowIndex % 2 ? "#f6f8fa" : "#fff" }}>
                      {(Array.isArray(row) ? row : []).map((cell, cellIndex) => (
                        <td key={cellIndex} style={{ padding: "6px 8px", borderTop: "1px solid #d8dee4" }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.type === "text" && block.text) {
          return (
            <pre key={`text-${index}`} style={{ margin: 0, padding: 10, borderRadius: 8, background: "#f6f8fa", color: "#1f2328", borderLeft: "3px solid #1a7f37", whiteSpace: "pre-wrap", fontSize: 12, fontFamily: "Consolas, monospace" }}>
              {block.text}
            </pre>
          );
        }
        return null;
      })}
    </div>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M7 1.2v11.6M1.2 7h11.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

const LINE_BOX = 36;
const MIN_LINES = 3;
const COLLAPSED_HEIGHT = 16 + 20 * MIN_LINES;

function ChevronIcon({ open }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      aria-hidden="true"
      style={{ transform: open ? "rotate(90deg)" : "none", display: "block" }}
    >
      <path d="M4 2.2 8 6 4 9.8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function useContentHeight(ref, deps, minHeight = LINE_BOX) {
  const [height, setHeight] = useState(minHeight);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const previous = node.style.height;
    node.style.height = "0px";
    const next = Math.max(node.scrollHeight, minHeight);
    node.style.height = previous;
    setHeight((current) => (current === next ? current : next));
    // Measured from the DOM node; deps are the values that change its text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return height;
}

const PYTHON_KEYWORDS = /^(?:False|None|True|and|as|assert|async|await|break|class|continue|def|del|elif|else|except|finally|for|from|global|if|import|in|is|lambda|nonlocal|not|or|pass|raise|return|try|while|with|yield)\b/;
const PYTHON_BUILTINS = new Set(["print", "len", "range", "int", "str", "float", "bool", "list", "dict", "set", "tuple", "type", "isinstance", "enumerate", "zip", "map", "filter", "sorted", "sum", "min", "max", "abs", "round", "open", "super"]);

const pythonHighlight = HighlightStyle.define([
  { tag: tags.keyword, color: "#cf222e", fontWeight: "650" },
  { tag: tags.special(tags.variableName), color: "#8250df" },
  { tag: tags.definition(tags.variableName), color: "#953800" },
  { tag: tags.string, color: "#0a3069" },
  { tag: tags.comment, color: "#6e7781", fontStyle: "italic" },
  { tag: tags.number, color: "#0550ae" },
  { tag: tags.operator, color: "#cf222e" },
]);

const pythonLanguage = StreamLanguage.define({
  tokenTable: {
    keyword: tags.keyword,
    builtin: tags.special(tags.variableName),
    def: tags.definition(tags.variableName),
    string: tags.string,
    comment: tags.comment,
    number: tags.number,
    operator: tags.operator,
  },
  startState() {
    return { quote: null };
  },
  token(stream, state) {
    if (state.quote) {
      const quote = state.quote;
      while (!stream.eol()) {
        const ch = stream.next();
        if (ch === "\\") {
          stream.next();
          continue;
        }
        if (quote.length === 1 && ch === quote) {
          state.quote = null;
          break;
        }
        if (quote.length === 3 && ch === quote[0] && stream.match(quote.slice(1))) {
          state.quote = null;
          break;
        }
      }
      return "string";
    }
    if (stream.eatSpace()) return null;
    if (stream.match("#")) {
      stream.skipToEnd();
      return "comment";
    }
    if (stream.match('"""') || stream.match("'''")) {
      state.quote = stream.current();
      return "string";
    }
    if (stream.match('"') || stream.match("'")) {
      state.quote = stream.current();
      return "string";
    }
    if (stream.match(/^(?:0x[\da-fA-F]+|\d+\.?\d*)/)) return "number";
    if (stream.match(PYTHON_KEYWORDS)) return "keyword";
    if (stream.match(/^[A-Za-z_]\w*/)) {
      if (PYTHON_BUILTINS.has(stream.current())) return "builtin";
      if (stream.match(/^\s*\(/, false)) return "def";
      return null;
    }
    if (stream.match(/^[+\-*/%=<>!&|^~]+/)) return "operator";
    stream.next();
    return null;
  },
});

const pythonTheme = EditorView.theme({
  "&": { fontSize: "13px", backgroundColor: "#fff" },
  ".cm-content": { fontFamily: "Consolas, ui-monospace, monospace", caretColor: "#1f2328" },
  ".cm-gutters": { backgroundColor: "#f6f8fa", color: "#8b949e", borderRight: "1px solid #d0d7de" },
  ".cm-activeLine": { backgroundColor: "#fff8c5" },
  ".cm-activeLineGutter": { backgroundColor: "#fff8c5" },
  "&.cm-focused": { outline: "none" },
});

function CodeCellEditor({ value, disabled, wrap, collapsed, onChange, onRun }) {
  const extensions = useMemo(() => [
    pythonLanguage,
    pythonTheme,
    syntaxHighlighting(pythonHighlight),
    indentUnit.of("    "),
    Prec.high(keymap.of([
      {
        key: "Mod-Enter",
        run() {
          onRun();
          return true;
        },
      },
      {
        key: "Enter",
        run(view) {
          const line = view.state.doc.lineAt(view.state.selection.main.head);
          const indent = (line.text.match(/^[ \t]*/) || [""])[0];
          view.dispatch(view.state.replaceSelection(`\n${indent}`));
          return true;
        },
      },
    ])),
    ...(wrap ? [EditorView.lineWrapping] : []),
  ], [onRun, wrap]);

  return (
    <div style={{ border: "1px solid #d0d7de", borderRadius: 6, overflow: "hidden", background: "#fff" }}>
      <CodeMirror
        value={String(value || "")}
        editable={!disabled}
        indentWithTab
        basicSetup={{
          lineNumbers: true,
          foldGutter: false,
          highlightActiveLine: true,
          highlightActiveLineGutter: true,
          bracketMatching: true,
          closeBrackets: true,
          indentOnInput: true,
          autocompletion: false,
        }}
        theme="light"
        height={collapsed ? `${COLLAPSED_HEIGHT}px` : "auto"}
        minHeight={`${COLLAPSED_HEIGHT}px`}
        extensions={extensions}
        onChange={(next) => onChange(next)}
      />
    </div>
  );
}

function safeUrl(url) {
  const trimmed = String(url || "").trim();
  return /^(https?:|mailto:)/i.test(trimmed) ? trimmed : "";
}

function renderInline(text, keyPrefix) {
  const parts = [];
  const pattern = /(`[^`\n]+`)|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let match = pattern.exec(text);
  let index = 0;
  while (match) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1]) {
      parts.push(
        <code key={`${keyPrefix}-c${index}`} style={{ background: "#f6f8fa", padding: "0 4px", borderRadius: 4, fontFamily: "Consolas, monospace" }}>
          {match[1].slice(1, -1)}
        </code>
      );
    } else if (match[2]) {
      parts.push(<strong key={`${keyPrefix}-b${index}`}>{match[3]}</strong>);
    } else if (match[4]) {
      parts.push(<em key={`${keyPrefix}-i${index}`}>{match[5]}</em>);
    } else if (match[6]) {
      const href = safeUrl(match[8]);
      parts.push(href
        ? <a key={`${keyPrefix}-a${index}`} href={href} target="_blank" rel="noreferrer">{match[7]}</a>
        : match[7]);
    }
    last = match.index + match[0].length;
    index += 1;
    match = pattern.exec(text);
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function renderMarkdown(source) {
  const lines = String(source || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let index = 0;
  let key = 0;
  while (index < lines.length) {
    const line = lines[index];
    if (line.trim().startsWith("```")) {
      const code = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push(
        <pre key={key} style={{ margin: "8px 0", padding: 8, background: "#f6f8fa", borderRadius: 6, overflow: "auto" }}>
          <code style={{ fontFamily: "Consolas, monospace" }}>{code.join("\n")}</code>
        </pre>
      );
      key += 1;
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const Tag = level === 1 ? "h2" : level === 2 ? "h3" : "h4";
      const fontSize = level === 1 ? 22 : level === 2 ? 18 : 16;
      blocks.push(
        <Tag key={key} style={{ fontSize, margin: "8px 0", fontWeight: 650 }}>
          {renderInline(heading[2], `h${key}`)}
        </Tag>
      );
      key += 1;
      index += 1;
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(<li key={index}>{renderInline(lines[index].replace(/^\s*[-*]\s+/, ""), `ul${key}-${index}`)}</li>);
        index += 1;
      }
      blocks.push(<ul key={key} style={{ margin: "8px 0", paddingLeft: 20 }}>{items}</ul>);
      key += 1;
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (index < lines.length && /^\s*\d+\.\s+/.test(lines[index])) {
        items.push(<li key={index}>{renderInline(lines[index].replace(/^\s*\d+\.\s+/, ""), `ol${key}-${index}`)}</li>);
        index += 1;
      }
      blocks.push(<ol key={key} style={{ margin: "8px 0", paddingLeft: 20 }}>{items}</ol>);
      key += 1;
      continue;
    }
    if (!line.trim()) {
      index += 1;
      continue;
    }
    const paragraph = [line];
    index += 1;
    while (
      index < lines.length
      && lines[index].trim()
      && !/^(#{1,3})\s+/.test(lines[index])
      && !/^\s*[-*]\s+/.test(lines[index])
      && !/^\s*\d+\.\s+/.test(lines[index])
      && !lines[index].trim().startsWith("```")
    ) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push(
      <p key={key} style={{ margin: "8px 0" }}>
        {renderInline(paragraph.join(" "), `p${key}`)}
      </p>
    );
    key += 1;
  }
  return blocks;
}

function MarkdownView({ source, collapsed }) {
  return (
    <div
      style={{
        minHeight: collapsed ? COLLAPSED_HEIGHT : LINE_BOX,
        maxHeight: collapsed ? COLLAPSED_HEIGHT : undefined,
        overflow: collapsed ? "hidden" : "visible",
        padding: "4px 2px",
        cursor: "text",
        fontSize: 14,
        lineHeight: 1.5,
      }}
    >
      {renderMarkdown(source)}
    </div>
  );
}

function MarkdownCellEditor({ value, disabled, collapsed, onChange, onBlur }) {
  const textRef = useRef(null);
  const contentHeight = useContentHeight(textRef, [value]);
  return (
    <textarea
      ref={textRef}
      value={value}
      disabled={disabled}
      autoFocus
      onBlur={onBlur}
      onChange={(event) => onChange(event.target.value)}
      style={{
        width: "100%",
        height: collapsed ? COLLAPSED_HEIGHT : contentHeight,
        minHeight: collapsed ? COLLAPSED_HEIGHT : LINE_BOX,
        resize: "none",
        overflow: "hidden",
        border: "1px solid #d0d7de",
        borderRadius: 6,
        padding: "8px 10px",
        fontSize: 14,
        lineHeight: "20px",
        background: "#fff",
      }}
    />
  );
}

function AddCellGap({ onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minHeight: 28 }}>
      <span style={{ flex: 1, height: 1, background: open ? "#d0d7de" : "transparent" }} />
      {open ? (
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              onAdd("markdown");
              setOpen(false);
            }}
          >
            Markdown
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              onAdd("code");
              setOpen(false);
            }}
          >
            Code
          </button>
        </div>
      ) : (
        <button
          type="button"
          aria-label="Add cell"
          onClick={() => setOpen(true)}
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            border: "1px solid #c5cdd6",
            background: "#fff",
            color: "#334155",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          <PlusIcon />
        </button>
      )}
      <span style={{ flex: 1, height: 1, background: open ? "#d0d7de" : "transparent" }} />
    </div>
  );
}

const MarimoNotebookComponent = forwardRef(function MarimoNotebookComponent(
  {
    notebookSource,
    setNotebookSource,
    notebookTitle,
    setNotebookTitle,
    notebookSequence,
    setNotebookSequence,
    notebookTime,
    setNotebookTime,
    notebookStatus,
    setNotebookStatus,
    resetKey,
    isReadOnly = false,
  },
  ref
) {
  const [allowAddCells, setAllowAddCells] = useState(true);
  const [maxCells, setMaxCells] = useState(20);
  const [cells, setCells] = useState(() => emptyNotebook().cells);
  const [selectedId, setSelectedId] = useState("");
  const [runNotes, setRunNotes] = useState({});
  const [validationById, setValidationById] = useState({});
  const [wrapById, setWrapById] = useState({});
  const [collapsedById, setCollapsedById] = useState({});
  const serializedRef = useRef("");
  const cellsRef = useRef(emptyNotebook().cells);
  const settingsRef = useRef({ allowAddCells: true, maxCells: 20 });
  const runningRef = useRef(new Set());

  const applyDocument = (doc, rawSource) => {
    const nextAllow = doc.allowAddCells !== false;
    const nextMax = Number(doc.maxCells) > 0 ? Number(doc.maxCells) : 20;
    const nextCells = doc.cells;
    settingsRef.current = { allowAddCells: nextAllow, maxCells: nextMax };
    cellsRef.current = nextCells;
    setAllowAddCells(nextAllow);
    setMaxCells(nextMax);
    setCells(nextCells);
    setSelectedId("");
    serializedRef.current = rawSource || serializeNotebook(doc);
    setRunNotes({});
    setValidationById({});
  };

  useEffect(() => {
    const raw = String(notebookSource || "");
    if (raw && raw === serializedRef.current) return;
    const next = raw.trim() ? parseNotebook(raw) : emptyNotebook();
    applyDocument(next, raw.trim() ? raw : serializeNotebook(next));
    // Reload when the parent switches notebooks or finishes a fetch.
    // Local edits write the same string into notebookSource and are ignored above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, notebookSource]);

  const commit = (nextCells, nextAllow = settingsRef.current.allowAddCells, nextMax = settingsRef.current.maxCells) => {
    settingsRef.current = { allowAddCells: nextAllow, maxCells: nextMax };
    cellsRef.current = nextCells;
    setAllowAddCells(nextAllow);
    setMaxCells(nextMax);
    setCells(nextCells);
    const serialized = serializeNotebook({
      allowAddCells: nextAllow,
      maxCells: nextMax,
      cells: nextCells,
    });
    serializedRef.current = serialized;
    setNotebookSource(serialized);
  };

  useImperativeHandle(ref, () => ({
    getSource: () => serializeNotebook({
      allowAddCells: settingsRef.current.allowAddCells,
      maxCells: settingsRef.current.maxCells,
      cells: cellsRef.current,
    }),
    getTests: () => {
      const mapped = {};
      cellsRef.current.forEach((cell) => {
        if (!cell || cell.type === "markdown" || !cell.id) return;
        const tests = Array.isArray(cell.tests) ? cell.tests : [];
        if (tests.length) mapped[cell.id] = tests;
      });
      return mapped;
    },
    getTitle: () => notebookTitle,
    getSequence: () => notebookSequence,
    getTime: () => notebookTime,
    getStatus: () => notebookStatus,
    validateSaveFields: () => {
      if (!String(notebookTitle || "").trim()) return false;
      if (!Number(notebookSequence) || Number(notebookSequence) <= 0) return false;
      const timeStr = String(notebookTime ?? "").trim();
      if (!timeStr || parseInt(timeStr, 10) < 0) return false;
      if (!String(notebookStatus || "").trim()) return false;
      return cells.length > 0;
    },
  }), [allowAddCells, maxCells, cells, notebookTitle, notebookSequence, notebookTime, notebookStatus]);

  const canAdd = !isReadOnly && allowAddCells && cells.length < Number(maxCells || 0);
  const links = cellLinks(cells);

  const addCell = (type, index) => {
    if (!canAdd) return;
    const id = `${type}-${Date.now()}`;
    const cell = { id, type, readOnly: false, source: "", tests: [] };
    const current = cellsRef.current;
    const at = Number.isInteger(index) ? index : current.length;
    const next = [...current.slice(0, at), cell, ...current.slice(at)];
    commit(next);
    setSelectedId(id);
  };

  const updateCell = (id, patch) => {
    commit(cellsRef.current.map((cell) => (cell.id === id ? { ...cell, ...patch } : cell)));
  };

  const queueRun = async (id) => {
    if (runningRef.current.has(id)) return;
    runningRef.current.add(id);
    const current = cellsRef.current;
    const index = current.findIndex((cell) => cell.id === id);
    const codeCells = current.slice(0, index + 1)
      .filter((cell) => cell.type === "code")
      .map((cell) => ({ id: cell.id, source: cell.source || "" }));
    setRunNotes((prev) => ({
      ...prev,
      [id]: { pending: true, ok: true, text: "Running on marimo-validator…" },
    }));
    try {
      const data = await runNotebookCellsOnValidator(id, codeCells);
      setRunNotes((prev) => ({
        ...prev,
        [id]: {
          pending: false,
          ok: !!data.ok,
          text: data.ok ? (data.output || "(no output)") : (data.error || "Validation failed."),
          outputs: data.ok && Array.isArray(data.outputs) ? data.outputs : [],
        },
      }));
    } catch (error) {
      const detail = error?.message || "Could not reach the marimo-validator.";
      setRunNotes((prev) => ({
        ...prev,
        [id]: { pending: false, ok: false, text: String(detail), outputs: [] },
      }));
    } finally {
      runningRef.current.delete(id);
    }
  };

  const executeCell = async (id) => {
    const current = cellsRef.current;
    const index = current.findIndex((cell) => cell.id === id);
    const codeCells = current.slice(0, index + 1)
      .filter((cell) => cell.type === "code")
      .map((cell) => ({ id: cell.id, source: cell.source || "" }));
    return runNotebookCellsOnValidator(id, codeCells);
  };

  const validateCell = async (id) => {
    if (runningRef.current.has(id)) return;
    const cell = cellsRef.current.find((item) => item.id === id);
    const tests = Array.isArray(cell?.tests) ? cell.tests : [];
    if (!tests.length) {
      setValidationById((prev) => ({
        ...prev,
        [id]: { pending: false, selectedId: "", cases: [], message: "Add a test case before validating this cell." },
      }));
      return;
    }
    runningRef.current.add(id);
    setValidationById((prev) => ({
      ...prev,
      [id]: { pending: true, selectedId: tests[0]?.id || "", cases: [], message: "" },
    }));
    try {
      const current = cellsRef.current;
      const index = current.findIndex((cell) => cell.id === id);
      const codeSources = current
        .slice(0, index + 1)
        .filter((cell) => cell.type === "code")
        .map((cell) => cell.source || "");
      const prefixSources = codeSources.slice(0, -1);
      const targetSource = codeSources[codeSources.length - 1] || "";
      const functionCall = current[index]?.functionCall || "";
      const cases = await validateNotebookCellTests({
        cellId: id,
        prefixSources,
        targetSource,
        functionCall,
        tests,
      });
      const lastRunnable = cases[cases.length - 1];
      setRunNotes((prev) => ({
        ...prev,
        [id]: {
          pending: false,
          ok: !!lastRunnable?.passed,
          text: lastRunnable?.passed
            ? (lastRunnable.actual || "(no output)")
            : (lastRunnable?.error || lastRunnable?.actual || "Validation failed."),
          outputs: [],
        },
      }));
      setValidationById((prev) => ({
        ...prev,
        [id]: { pending: false, selectedId: cases[0]?.id || "", cases, message: "" },
      }));
    } catch (error) {
      const detail = error?.message || "Could not reach the marimo-validator.";
      setValidationById((prev) => ({
        ...prev,
        [id]: {
          pending: false,
          selectedId: tests[0]?.id || "",
          cases: scoreTests(tests, { ok: false, error: String(detail) }),
          message: "",
        },
      }));
    } finally {
      runningRef.current.delete(id);
    }
  };

  const updateTests = (cellId, tests) => {
    updateCell(cellId, { tests });
    setValidationById((prev) => {
      if (!prev[cellId]) return prev;
      const next = { ...prev };
      delete next[cellId];
      return next;
    });
  };

  const testsFor = (cellId) => {
    const cell = cellsRef.current.find((item) => item.id === cellId);
    return Array.isArray(cell?.tests) ? cell.tests : [];
  };

  const addTest = (cellId) => {
    updateTests(cellId, [...testsFor(cellId), {
      Value: [""],
      Output: "",
      title: "",
      description: "",
      errorMessage: "",
    }]);
  };

  const changeTestMeta = (cellId, testIndex, patch) => {
    updateTests(cellId, testsFor(cellId).map((test, index) => (
      index === testIndex ? { ...test, ...patch } : test
    )));
  };

  const removeTest = (cellId, testIndex) => {
    updateTests(cellId, testsFor(cellId).filter((_, index) => index !== testIndex));
  };

  const changeTestOutput = (cellId, testIndex, output) => {
    updateTests(cellId, testsFor(cellId).map((test, index) => (
      index === testIndex ? { ...test, Output: output } : test
    )));
  };

  const changeTestValue = (cellId, testIndex, valueIndex, value) => {
    updateTests(cellId, testsFor(cellId).map((test, index) => {
      if (index !== testIndex) return test;
      const values = (test.Value || [""]).slice();
      values[valueIndex] = value;
      return { ...test, Value: values };
    }));
  };

  const addTestValue = (cellId, testIndex) => {
    updateTests(cellId, testsFor(cellId).map((test, index) => (
      index === testIndex ? { ...test, Value: [...(test.Value || []), ""] } : test
    )));
  };

  const removeTestValue = (cellId, testIndex, valueIndex) => {
    updateTests(cellId, testsFor(cellId).map((test, index) => (
      index === testIndex
        ? { ...test, Value: (test.Value || []).filter((_, itemIndex) => itemIndex !== valueIndex) }
        : test
    )));
  };

  const moveCell = (id, direction) => {
    if (isReadOnly) return;
    const current = cellsRef.current;
    const index = current.findIndex((cell) => cell.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= current.length) return;
    const next = current.slice();
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    commit(next);
  };

  const removeCell = (id) => {
    if (isReadOnly || cellsRef.current.length <= 1) return;
    const next = cellsRef.current.filter((cell) => cell.id !== id);
    commit(next);
    setSelectedId((current) => (current === id ? next[0]?.id || "" : current));
  };

  const handleUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    const isIpynb = String(file.name || "").toLowerCase().endsWith(".ipynb");
    reader.onload = () => {
      const text = String(reader.result || "");
      try {
        const py = isIpynb ? marimoPyFromIpynb(text) : text;
        const next = parseNotebook(py);
        applyDocument(next, "");
        commit(next.cells, next.allowAddCells, next.maxCells);
      } catch (error) {
        window.alert(error?.message || "Could not import this notebook.");
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  return (
    <div style={{ display: "flex", width: "100%", height: "70vh", minHeight: 480, gap: 8 }}>
      <aside style={{ width: 280, minWidth: 240, overflow: "auto", border: "1px solid #ddd", borderRadius: 8, padding: 12, background: "#fff" }}>
        <label className="form-label">Title</label>
        <input
          className="form-control custom-input mb-2"
          value={notebookTitle || ""}
          disabled={isReadOnly}
          onChange={(e) => setNotebookTitle(e.target.value)}
        />
        <label className="form-label">Sequence</label>
        <input
          type="number"
          className="form-control custom-input mb-2"
          value={notebookSequence || ""}
          disabled={isReadOnly}
          onChange={(e) => setNotebookSequence(e.target.value)}
        />
        <label className="form-label">Time (minutes) <span className="text-danger">*</span></label>
        <input
          type="number"
          min="0"
          className="form-control custom-input mb-2"
          value={notebookTime || ""}
          disabled={isReadOnly}
          placeholder="Enter time in minutes (e.g., 10)"
          onChange={(e) => setNotebookTime(e.target.value)}
        />
        <label className="form-label">Status <span className="text-danger">*</span></label>
        <select
          className="form-control custom-input mb-2"
          value={notebookStatus || "active"}
          disabled={isReadOnly}
          onChange={(e) => setNotebookStatus(e.target.value)}
        >
          <option value="active">Active</option>
          <option value="not active">Not Active</option>
        </select>
        <label className="form-label">Upload .ipynb or .py</label>
        <input
          type="file"
          accept=".ipynb,.py,application/json,text/x-python,text/plain"
          className="form-control mb-3"
          disabled={isReadOnly}
          onChange={handleUpload}
        />
        <div className="form-check mb-2">
          <input
            id="allowAddCells"
            className="form-check-input"
            type="checkbox"
            checked={allowAddCells}
            disabled={isReadOnly}
            onChange={(e) => {
              setAllowAddCells(e.target.checked);
              commit(cellsRef.current, e.target.checked, maxCells);
            }}
          />
          <label className="form-check-label" htmlFor="allowAddCells">
            Allow cell growth
          </label>
        </div>
        <label className="form-label">Max cells</label>
        <input
          type="number"
          min={1}
          className="form-control custom-input mb-3"
          value={maxCells}
          disabled={isReadOnly}
          onChange={(e) => {
            const value = Number(e.target.value) || 1;
            setMaxCells(value);
            commit(cellsRef.current, allowAddCells, value);
          }}
        />
        <div>
          <div className="fw-semibold mb-1">Cell tree</div>
          {links.length === 0 ? (
            <p className="text-muted mb-0" style={{ fontSize: 12 }}>
              No shared names yet. Assign a variable in one code cell and use it in a later cell.
            </p>
          ) : (
            <ul className="mb-0 ps-3" style={{ fontSize: 12 }}>
              {links.map((link) => {
                const fromIndex = cells.findIndex((cell) => cell.id === link.from) + 1;
                const toIndex = cells.findIndex((cell) => cell.id === link.to) + 1;
                return (
                  <li key={`${link.from}-${link.to}-${link.name}`}>
                    Cell {toIndex} uses {link.name} from cell {fromIndex}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", padding: "8px 4px" }}>
        {canAdd && <AddCellGap onAdd={(type) => addCell(type, 0)} />}
        {cells.map((cell, index) => {
          const isCode = cell.type === "code";
          return (
            <React.Fragment key={cell.id}>
              <fieldset
                onClick={() => setSelectedId(cell.id)}
                style={{
                  position: "relative",
                  margin: 0,
                  border: `1px solid ${cell.id === selectedId ? "#8b949e" : "#d0d7de"}`,
                  borderLeft: `3px solid ${isCode ? "#1a7f37" : "#8250df"}`,
                  borderRadius: 8,
                  background: isCode ? "#f6f8fa" : "#fff",
                  padding: "14px 12px 12px",
                }}
              >
                <legend
                  style={{
                    float: "none",
                    width: "auto",
                    margin: 0,
                    marginLeft: 8,
                    padding: "0 6px",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    color: isCode ? "#1a7f37" : "#8250df",
                    background: isCode ? "#f6f8fa" : "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <button
                    type="button"
                    aria-label={collapsedById[cell.id] ? "Expand cell" : "Collapse cell"}
                    aria-expanded={!collapsedById[cell.id]}
                    onClick={(event) => {
                      event.stopPropagation();
                      setCollapsedById((prev) => ({ ...prev, [cell.id]: !prev[cell.id] }));
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#57606a",
                      width: 16,
                      height: 16,
                      padding: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ChevronIcon open={!collapsedById[cell.id]} />
                  </button>
                  {isCode ? "Code" : "Markdown"}
                </legend>
                <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                  <div className="d-flex align-items-center gap-1">
                    <button
                      type="button"
                      aria-label="Move cell up"
                      disabled={isReadOnly || index === 0}
                      onClick={() => moveCell(cell.id, -1)}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 6,
                        border: "1px solid #c5cdd6",
                        background: "#fff",
                        color: "#334155",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                        <path d="M6 2.2 10 8H2L6 2.2Z" fill="currentColor" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      aria-label="Move cell down"
                      disabled={isReadOnly || index === cells.length - 1}
                      onClick={() => moveCell(cell.id, 1)}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 6,
                        border: "1px solid #c5cdd6",
                        background: "#fff",
                        color: "#334155",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                        <path d="M6 9.8 2 4h8L6 9.8Z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                  <label className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={!!cell.readOnly}
                      disabled={isReadOnly}
                      onChange={(e) => updateCell(cell.id, { readOnly: e.target.checked })}
                    />
                    <span className="form-check-label ms-1">Read only</span>
                  </label>
                  {isCode && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={isReadOnly || !!runNotes[cell.id]?.pending || !!validationById[cell.id]?.pending}
                      onClick={() => validateCell(cell.id)}
                    >
                      {validationById[cell.id]?.pending ? "Validating" : "Validate"}
                    </button>
                  )}
                  {isCode && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      disabled={isReadOnly || !!runNotes[cell.id]?.pending || !!validationById[cell.id]?.pending}
                      onClick={() => queueRun(cell.id)}
                    >
                      {runNotes[cell.id]?.pending ? "Running" : "Run"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={isReadOnly || cells.length <= 1}
                    onClick={() => removeCell(cell.id)}
                  >
                    Remove
                  </button>
                  {isCode && (
                    <button
                      type="button"
                      className={`btn btn-sm ${wrapById[cell.id] ? "btn-secondary" : "btn-outline-secondary"}`}
                      aria-pressed={!!wrapById[cell.id]}
                      onClick={() => setWrapById((prev) => ({ ...prev, [cell.id]: !prev[cell.id] }))}
                    >
                      Wrap
                    </button>
                  )}
                  </div>
                </div>
                {isCode ? (
                  <CodeCellEditor
                    value={cell.source || ""}
                    disabled={isReadOnly}
                    wrap={!!wrapById[cell.id]}
                    collapsed={!!collapsedById[cell.id]}
                    onChange={(source) => updateCell(cell.id, { source })}
                    onRun={() => queueRun(cell.id)}
                  />
                ) : cell.id === selectedId ? (
                  <MarkdownCellEditor
                    value={cell.source || ""}
                    disabled={isReadOnly}
                    collapsed={!!collapsedById[cell.id]}
                    onChange={(source) => updateCell(cell.id, { source })}
                    onBlur={(event) => {
                      const cellRoot = event.currentTarget.closest("fieldset");
                      if (cellRoot && event.relatedTarget && cellRoot.contains(event.relatedTarget)) return;
                      setSelectedId((current) => (current === cell.id ? "" : current));
                    }}
                  />
                ) : (
                  <MarkdownView source={cell.source || ""} collapsed={!!collapsedById[cell.id]} />
                )}
                {isCode && runNotes[cell.id] && <RunOutput note={runNotes[cell.id]} />}
              </fieldset>
              {isCode && (
                <div style={{ marginTop: 8, border: "1px solid #d0d7de", borderRadius: 8, background: "#fff", padding: 12 }}>
                  <div className="form-group mb-2">
                    <label style={{ fontSize: 12 }}>Function call (optional, py-executor style):</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      disabled={isReadOnly}
                      value={cell.functionCall || ""}
                      placeholder='e.g. print(get_day_type("Monday"))'
                      onChange={(event) => updateCell(cell.id, { functionCall: event.target.value })}
                    />
                  </div>
                  <PythonTestCases
                    testCases={cell.tests || []}
                    disabled={isReadOnly}
                    addTestCase={() => addTest(cell.id)}
                    removeTestCase={(testIndex) => removeTest(cell.id, testIndex)}
                    handleTestCaseOutputChange={(testIndex, output) => changeTestOutput(cell.id, testIndex, output)}
                    handleTestCaseValueChange={(testIndex, valueIndex, value) => changeTestValue(cell.id, testIndex, valueIndex, value)}
                    addTestCaseValue={(testIndex) => addTestValue(cell.id, testIndex)}
                    removeTestCaseValue={(testIndex, valueIndex) => removeTestValue(cell.id, testIndex, valueIndex)}
                    onMetaChange={(testIndex, patch) => changeTestMeta(cell.id, testIndex, patch)}
                  />
                  {validationById[cell.id] && (
                    <ValidationReport
                      report={validationById[cell.id]}
                      onSelect={(testId) => setValidationById((prev) => ({
                        ...prev,
                        [cell.id]: { ...prev[cell.id], selectedId: testId },
                      }))}
                    />
                  )}
                </div>
              )}
              {canAdd && <AddCellGap onAdd={(type) => addCell(type, index + 1)} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
});

export default MarimoNotebookComponent;
